import { useDispatch } from 'react-redux';
import { userStates } from '@/constants/userStates';
import { setNotifications, setUser, updateUserStateKvp } from '@/actions/user';
import { useCallback } from 'react';
import { useRef } from 'react';
import {
  setAllStudents,
  setChildren,
  switchChild,
  switchSchool
} from '@/actions/school';
import { HOME_SCHOOL_IDS } from '@/config/settings';
import { toaster } from '@/components/common';
import { useNavigate } from 'react-router';
import CachedApi from '@/cache/CachedApi';
import { cache } from '@/store';
import types from '@/cache/types';
import { setRoutineState } from '@/actions/routine';
import { setCoinsState } from '@/actions/coins';
import { setShopItems } from '@/actions/shopItems';

const useLoadUserContext = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userRef = useRef({ loading: false, context: null });

  const setExperimentStatuses = context => {
    if (!context || context?.userRole !== 'Student') return false;

    const statuses = {
      isAccountDisabled: false,
      isStarLinkedDynamicQuizCompleted: !!context.userState?.find(
        s =>
          s.key === userStates.isStarLinkedDynamicQuizCompleted &&
          s.value === 'true'
      ),
      isEediFamilyDisabled: !!context.featureFlagSchools?.find(
        s => s.eediHomeFeature === 'Disabled'
      ),
      isStarLinkedDynamicQuizTreatment: !!context.featureFlagSchools?.find(
        s =>
          s.experimentCohortA === 'Enabled' &&
          s.autoInterventionFlowFeature !== 'Enabled'
      ),
      isStarLinkedDynamicQuizControl: !!context.featureFlagSchools?.find(
        s =>
          s.experimentCohortB === 'Enabled' &&
          s.autoInterventionFlowFeature !== 'Enabled'
      ),
      // Post-experiment: only two intervention arms remain. Cohort D = RCT
      // direct-to-AI arm, cohort E = opted out of AI (typically school-level).
      // No flag = BAU (tutor-moderated AI permitted).
      isGDMHypercontextAndPedagogyArm: !!context.featureFlagSchools?.find(
        s =>
          s.autoInterventionFlowFeature === 'Enabled' &&
          s.experimentCohortD === 'Enabled'
      ),
      isGDMHumanOnlyArm: !!context.featureFlagSchools?.find(
        s =>
          s.autoInterventionFlowFeature === 'Enabled' &&
          s.experimentCohortE === 'Enabled'
      )
    };

    // Single flag to check if the user is in an intervention-enabled school
    statuses.isGDMSchool =
      statuses.isGDMHypercontextAndPedagogyArm || statuses.isGDMHumanOnlyArm;

    console.table([
      {
        Feature: 'experimentCohortA - Star Dynamic Quiz Treatment',
        Status: statuses.isStarLinkedDynamicQuizTreatment
      },
      {
        Feature: 'experimentCohortB - Star Dynamic Quiz Control',
        Status: statuses.isStarLinkedDynamicQuizControl
      },
      {
        Feature: 'isStarLinkedDynamicQuizCompleted',
        Status: statuses.isStarLinkedDynamicQuizCompleted
      },
      {
        Feature: 'isEediFamilyDisabled',
        Status: statuses.isEediFamilyDisabled
      },
      {
        Feature: 'User Context - User State',
        Status: context.userState
      },
      {
        Feature: 'isGDMHypercontextAndPedagogyArm',
        Status: statuses.isGDMHypercontextAndPedagogyArm
      },
      {
        Feature: 'isGDMHumanOnlyArm',
        Status: statuses.isGDMHumanOnlyArm
      },
      {
        Feature: 'isGDMSchool',
        Status: statuses.isGDMSchool
      }
    ]);

    if (statuses.isStarLinkedDynamicQuizControl) {
      statuses.isAccountDisabled =
        statuses.isEediFamilyDisabled &&
        statuses.isStarLinkedDynamicQuizCompleted;
    } else {
      statuses.isAccountDisabled = statuses.isEediFamilyDisabled;
    }

    return statuses;
  };

  const setStudentContext = useCallback(async () => {
    const context = userRef.current.context;

    if (!context || context?.userRole !== 'Student') return;

    try {
      // Do not dispatch `isAccountDisabled` from here — `setUserContext` owns
      // it, including the control-cohort carve-out.
      const familyOrDefaultSchool =
        context.students.find(s => HOME_SCHOOL_IDS.includes(s.schoolId)) ??
        context.students[0];

      dispatch(
        switchSchool(
          familyOrDefaultSchool?.schoolId,
          familyOrDefaultSchool?.studentId
        )
      );

      dispatch(setAllStudents(context?.students ?? []));

      dispatch(setShopItems(context?.userItems ?? []));

      if (context?.routineState) {
        dispatch(setRoutineState(context?.routineState));
      }

      if (context?.transactionInsights) {
        dispatch(setCoinsState(context?.transactionInsights));
      }

      if (context?.notifications) {
        dispatch(setNotifications(context?.notifications));
      }

      if (!context.acceptTermsAndConditions) {
        navigate('/terms');
      } else {
        const from = localStorage.getItem('from');
        if (from) {
          localStorage.removeItem('from');
          navigate(from);
        }
      }
    } catch (e) {
      console.error(e);
      toaster.danger('Error setting student context!');
    }
  }, [dispatch, navigate]);

  const setParentContext = useCallback(async () => {
    const context = userRef.current.context;

    if (!context || context?.userRole !== 'Parent') return;

    try {
      const children =
        context.children?.map(c => {
          c.classroomStudents = c.students;
          return c;
        }) ?? [];

      dispatch(setChildren(children));

      if (children.length) {
        dispatch(switchSchool(children[0].schoolId, children[0].studentId));
        dispatch(switchChild(children[0].childId, true));
      }

      const from = localStorage.getItem('from');
      if (from) {
        localStorage.removeItem('from');
        navigate(from);
      }
    } catch (e) {
      console.error(e);
      toaster.danger('Error setting parent context!');
    }
  }, [dispatch, navigate]);

  const setUserContext = useCallback(
    async (isCached = false) => {
      userRef.current.loading = true;

      try {
        if (isCached)
          cache.bustTypeCache({ typenames: [types.userRoleContext.typename] });

        userRef.current.context = await CachedApi.getUserRoleContext();

        if (userRef.current.context) {
          dispatch(
            setUser({
              ...userRef.current.context,
              ...setExperimentStatuses(userRef.current.context)
            })
          );

          await setStudentContext();
          await setParentContext();

          if (userRef.current.context?.userRole !== 'Teacher') {
            dispatch(updateUserStateKvp(userStates.hasLoggedIn, 'true'));
          }
        }
      } catch (e) {
        console.error(e);
        toaster.danger('Error setting user context!');
      }

      userRef.current.loading = false;

      return userRef.current.context ?? {};
    },
    [dispatch, setParentContext, setStudentContext]
  );

  return async (isCached = false) => await setUserContext(isCached);
};

export default useLoadUserContext;
